import {
  forwardRef,
  type Key,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
  Children,
} from 'react';
import { IntlProvider } from 'react-intl';
import { isElement } from 'react-is';

import {
  type DataTestId,
  type MaskingProps,
  type StylingProps,
} from '@dynatrace/strato-components/core';
import { Flex } from '@dynatrace/strato-components/layouts';
import { getLanguage } from '@dynatrace-sdk/user-preferences';

import { FilterBarContext } from './contexts/FilterBarContext.js';
import { Item } from './Item.js';
import { MoreMenu } from './MoreMenu.js';
import { ResetButton } from './ResetButton.js';
import { reducer } from './state/filter-state-reducer.js';
import {
  type FilterBarReducerAction,
  FilterBarActions,
} from './types/filter-state.js';
import {
  type FilterItemValues,
  FilterState,
  type PinnedState,
} from './types/shared-filter-types.js';
import { isSlot } from '../../core/utils/is-slot.js';

// #region Typings
/**
 * Props for the Filter component.
 * @public
 */
export interface BaseFilterBarProps
  extends StylingProps,
    DataTestId,
    MaskingProps {
  /** Handler that is called when the value of the sub-filter changes. */
  onFilterChange: (value: FilterItemValues) => void;
  /**
   * All sub-filters connected to this filter.
   * Only components compatible with FilterItemProps are supported!
   */
  children: ReactNode;
  /** The element's unique identifier. See [MDN](https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/id). */
  id?: string;
  /** Defines if the labels are shown for the filter item. If set to false the label will be placed as aria-label instead. It can be overwritten for a single filter item by setting the showLabel-prop of the item as well. */
  showLabels?: boolean;
}

/**
 * @public
 */
export interface ControlledFilterBarProps {
  /** The current pinned state of the item. */
  pinnedState: PinnedState;
  /** Handler that is called when the pinned state changes. */
  onPinnedStateChange: (
    newState: PinnedState,
    changedItems: Array<string>,
  ) => void;
  /** Determines if a filter item is removable and its initial pinning state. */
  defaultPinnedState?: never;
}

/**
 * @public
 */
export interface UncontrolledFilterBarProps {
  /** The current pinned state of the item. */
  pinnedState?: never;
  /** Handler that is called when the pinned state changes. */
  onPinnedStateChange?: (newState: PinnedState, changedItems: Key[]) => void;
  /**
   * Determines if a filter item is removable and its initial pinning state.
   * @defaultValue 'pinned'
   */
  defaultPinnedState?: PinnedState;
}

/**
 * @public
 */
export type FilterBarProps = (
  | ControlledFilterBarProps
  | UncontrolledFilterBarProps
) &
  BaseFilterBarProps;

/**
 * Defines slots into which children of the filter should be rendered.
 * The 'more' slot is used for rendering filter items in addition to the 'items' slot.
 */
type FilterBarSlots = {
  items: ReactNode[];
  more: ReactNode[];
  reset: ReactNode;
};
// #endregion

/** Iterates over child nodes to extract specific component slots. */
function getItemSlotsAndDefaultPinnedFilters(
  children: ReactNode,
  defaultPinnedState: PinnedState,
) {
  const slots: FilterBarSlots = {
    items: [],
    more: [],
    reset: null,
  };
  const defaultPinnedFilters: Key[] = [];

  for (const child of Children.toArray(children)) {
    if (isSlot(child, Item)) {
      const pinnedState = defaultPinnedState[child.props.name] ?? 'pinned';
      switch (pinnedState) {
        case 'optional':
          slots.more.push(child);
          break;
        case 'pinned-optional':
          defaultPinnedFilters.push(child.props.name);
          slots.more.push(child);
          break;
        default:
          slots.items.push(child);
      }
    } else if (isElement(child) && child.type === ResetButton) {
      slots.reset = child;
    } else if (child !== null) {
      // do nothing if the child is null (when rendered conditionally for example) and throw a warning otherwise
      // eslint-disable-next-line no-console
      console.warn('The used child is not supported by the FilterBar.');
    }
  }

  return { slots, defaultPinnedFilters };
}

const _FilterBar = /* @__PURE__ */ forwardRef<HTMLDivElement, FilterBarProps>(
  (props, forwardedRef) => {
    const {
      children,
      id,
      onFilterChange,
      onPinnedStateChange,
      defaultPinnedState,
      pinnedState,
      showLabels = true,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
    }: FilterBarProps = props;
    const language = getLanguage();
    const [lastAddedItem, setLastAddedItem] = useState<string | null>(null);
    const [isInitialized, setIsInitialized] = useState<boolean>(false);

    const callback = useCallback(
      (state: FilterState, action: FilterBarReducerAction) =>
        // eslint-disable-next-line @typescript-eslint/prefer-ts-expect-error, @typescript-eslint/ban-ts-comment
        // @ts-ignore Strict mode transition - needs to be investigated
        reducer(state, action, !!pinnedState, onPinnedStateChange),
      // pinnedState is excluded since if it would change the component would switch from controlled to uncontrolled.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [onFilterChange, onPinnedStateChange],
    );

    // Initialize state and action dispatcher of the filter
    const [state, dispatch] = useReducer(callback, {
      filters: undefined,
      itemsPinnedState: defaultPinnedState ?? pinnedState,
    });

    useEffect(() => {
      if (pinnedState) {
        dispatch({
          type: FilterBarActions.CHANGEPINNEDSTATE,
          payload: {
            newPinnedState: pinnedState,
          },
        });
      }
    }, [pinnedState]);

    // Since the filter items register themselves in the state, the state is correctly initialized after the first render.
    // To not trigger the onChange initially we need to set the starting point manually.
    useEffect(() => {
      if (state.filters === undefined) {
        return;
      }

      if (!isInitialized) {
        setIsInitialized(true);
      } else {
        onFilterChange(state.filters);
      }

      // onFilterChange callback dependency changes every render and is therefore excluded.
      // the changes in isInitialized should only be considered in the next real render and not rerender the filterbar.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.filters]);

    const { slots } = useMemo(
      () =>
        getItemSlotsAndDefaultPinnedFilters(
          children,
          defaultPinnedState ?? pinnedState ?? {},
        ),
      /**
       * Disabled for defaultPinnedState, since this should only be considered when rendered initially.
       */
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [children, pinnedState],
    );

    return (
      <FilterBarContext.Provider
        value={{
          state,
          showLabels,
          dispatch,
          lastAddedItem,
          setLastAddedItem,
          hasControlledPinnedState: !!pinnedState,
        }}
      >
        <Flex
          flexWrap="wrap"
          id={id}
          ref={forwardedRef}
          data-testid={dataTestId}
          data-dtrum-mask={dataDtrumMask}
          data-dtrum-allow={dataDtrumAllow}
          alignItems="end"
          className={consumerClassName}
          style={consumerStyle}
        >
          {slots.items}
          {/* Because the items in the slots are cloned, we need to reprovide intl. */}
          {slots.more?.length > 0 && (
            <IntlProvider locale={language}>
              <MoreMenu>{slots.more}</MoreMenu>
            </IntlProvider>
          )}
          {slots.reset}
        </Flex>
      </FilterBarContext.Provider>
    );
  },
);

(_FilterBar as typeof _FilterBar & { displayName: string }).displayName =
  'FilterBar';

/**
 * Use the `FilterBar` to filter a set of data based on
 * one or several criteria determined by the user.
 * The component allows you to add various form elements as filter controls.
 * @public
 */
export const FilterBar = Object.assign(_FilterBar, {
  Item,
  ResetButton,
});
